import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CalendarCheck, CheckCircle2, Loader2 } from "lucide-react";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const TZ = Intl.DateTimeFormat().resolvedOptions().timeZone;
const dayKey = (iso) => new Date(iso).toLocaleDateString("en-CA", { timeZone: TZ });
const fmtTime = (iso) => new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit", timeZone: TZ });
const fmtDay = (k) => new Date(`${k}T12:00:00`).toLocaleDateString([], { weekday: "short", day: "numeric", month: "short" });

export const EnquiryFields = ({ form, set, programmes, withChild = true, testId }) => {
  const f = (k, label, props = {}) => (
    <label className="block text-sm"><span className="font-medium text-slate-700">{label}</span>
      <Input className="mt-1 bg-white text-slate-900" value={form[k] || ""} onChange={(e) => set({ ...form, [k]: e.target.value })} data-testid={`${testId}-${k}`} {...props} />
    </label>
  );
  return (
    <div className="grid sm:grid-cols-2 gap-3 text-left">
      {f("parent_name", "Parent / guardian name *", { required: true })}
      {f("email", "Email *", { type: "email", required: true })}
      {f("phone", "Phone (with country code)", { type: "tel" })}
      {f("country", "Country")}
      {withChild && f("child_name", "Child's name *", { required: true })}
      {withChild && f("child_age", "Child's age")}
      {programmes?.length > 0 && (
        <label className="block text-sm sm:col-span-2"><span className="font-medium text-slate-700">Programme of interest</span>
          <select className="mt-1 w-full h-10 rounded-md border border-input bg-white px-3 text-slate-900" value={form.programme || ""} onChange={(e) => set({ ...form, programme: e.target.value })} data-testid={`${testId}-programme`}>
            <option value="">Not sure yet</option>{programmes.map((p) => <option key={p}>{p}</option>)}
          </select>
        </label>
      )}
      <label className="block text-sm sm:col-span-2"><span className="font-medium text-slate-700">Message</span>
        <Textarea className="mt-1 bg-white text-slate-900" rows={3} value={form.message || ""} onChange={(e) => set({ ...form, message: e.target.value })} data-testid={`${testId}-message`} />
      </label>
      <input type="text" tabIndex={-1} autoComplete="off" className="hidden" value={form.website || ""} onChange={(e) => set({ ...form, website: e.target.value })} aria-hidden="true" />
      <label className="flex items-start gap-2 text-sm text-slate-600 sm:col-span-2">
        <input type="checkbox" className="mt-1" checked={!!form.consent} onChange={(e) => set({ ...form, consent: e.target.checked })} data-testid={`${testId}-consent`} />
        <span>I agree that TheMatheMind may contact me about this enquiry and store my details as described in the <a href="/privacy-policy" className="underline">privacy policy</a>.</span>
      </label>
    </div>
  );
};

export const submitEnquiry = async (form, extra = {}) => {
  const res = await axios.post(`${API}/trial/enquiries`, { ...form, ...extra, visitor_timezone: TZ });
  return res.data;
};

export const BookingWidget = ({ testId = "booking" }) => {
  const [config, setConfig] = useState(null);
  const [slots, setSlots] = useState(null);
  const [day, setDay] = useState(null);
  const [slot, setSlot] = useState(null);
  const [form, setForm] = useState({ consent: false });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  const load = async () => {
    const c = (await axios.get(`${API}/trial/config`)).data;
    setConfig(c);
    const end = new Date(Date.now() + Math.min(c.max_days_ahead, 45) * 86400000).toISOString();
    const s = (await axios.get(`${API}/trial/slots`, { params: { start: new Date().toISOString(), end } })).data.slots;
    setSlots(s);
    setDay((d) => d || (s[0] && dayKey(s[0])));
  };
  useEffect(() => { load().catch(() => setSlots([])); }, []);

  const byDay = useMemo(() => (slots || []).reduce((m, s) => { (m[dayKey(s)] ||= []).push(s); return m; }, {}), [slots]);

  const submit = async (e) => {
    e.preventDefault();
    if (!slot) return toast.error("Please choose a time slot");
    setBusy(true);
    try {
      setDone(await submitEnquiry(form, { slot_start: slot }));
    } catch (err) {
      toast.error(err.response?.data?.detail?.[0]?.msg || err.response?.data?.detail || "Couldn't book - please try again");
      if (err.response?.status === 409) { setSlot(null); load(); }
    } finally { setBusy(false); }
  };

  if (done) return (
    <div className="rounded-2xl bg-white border border-emerald-200 p-8 text-center text-slate-800" data-testid={`${testId}-success`}>
      <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto" />
      <h3 className="mt-3 text-2xl font-bold font-['Manrope']">Your free trial is booked!</h3>
      <p className="mt-2 text-slate-600">{new Date(done.slot_start).toLocaleString([], { dateStyle: "full", timeStyle: "short", timeZone: TZ })} ({TZ})</p>
      <p className="mt-2 text-sm text-slate-500">A confirmation email with a calendar invite is on its way to {form.email}.</p>
    </div>
  );

  return (
    <form onSubmit={submit} className="rounded-2xl bg-white border border-slate-200 shadow-xl p-5 sm:p-8 text-slate-800 space-y-6" data-testid={testId}>
      <div>
        <h3 className="flex items-center gap-2 font-bold text-lg font-['Manrope']"><CalendarCheck className="w-5 h-5 text-[var(--site-primary)]" />1. Pick a day & time</h3>
        <p className="text-xs text-slate-500 mt-1">Times shown in your time zone: {TZ}. Sessions are online, {config?.slot_minutes || 30} minutes.</p>
        {slots === null ? <Loader2 className="w-5 h-5 animate-spin mt-4" /> : !slots.length ? (
          <p className="mt-4 text-sm text-slate-600" data-testid={`${testId}-no-slots`}>No free slots right now - send us an enquiry below and we'll find a time.</p>
        ) : (
          <>
            <div className="flex gap-2 overflow-x-auto py-3" data-testid={`${testId}-days`}>
              {Object.keys(byDay).map((k) => (
                <button type="button" key={k} onClick={() => { setDay(k); setSlot(null); }} data-testid={`${testId}-day-${k}`}
                  className={`shrink-0 rounded-xl border px-3 py-2 text-sm transition-colors ${day === k ? "bg-[var(--site-primary)] text-white border-transparent" : "bg-white hover:border-[var(--site-primary)]"}`}>
                  <span className="block font-semibold">{fmtDay(k)}</span><span className="block text-[11px] opacity-80">{byDay[k].length} slots</span>
                </button>
              ))}
            </div>
            <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
              {(byDay[day] || []).map((s) => (
                <button type="button" key={s} onClick={() => setSlot(s)} data-testid={`${testId}-slot-${s}`}
                  className={`rounded-lg border py-2 text-sm font-medium transition-colors ${slot === s ? "bg-[var(--site-accent)] border-transparent text-slate-900" : "hover:border-[var(--site-primary)]"}`}>{fmtTime(s)}</button>
              ))}
            </div>
          </>
        )}
      </div>
      <div>
        <h3 className="font-bold text-lg font-['Manrope'] mb-3">2. Your details</h3>
        <EnquiryFields form={form} set={setForm} programmes={config?.programmes} testId={testId} />
      </div>
      <Button type="submit" disabled={busy || !form.consent || !slot} className="w-full h-12 rounded-full text-base bg-[var(--site-primary)] hover:brightness-110" data-testid={`${testId}-submit`}>
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Book my free trial"}
      </Button>
    </form>
  );
};
